import React, { useMemo, useState } from 'react';

type PropertyTypeMarketRow = {
  propertyType?: string;
  median?: number | null;
  average?: number | null;
  medianPerSqFt?: number | null;
  averagePerSqFt?: number | null;
  medianDaysOnMarket?: number | null;
  averageDaysOnMarket?: number | null;
  totalListings?: number | null;
};

type FilterMetric = 'rent' | 'sqft' | 'dom' | 'listings';

const money = (value: number) => `$${Math.round(value).toLocaleString()}`;

const displayPropertyType = (value?: string) => {
  if (!value) return 'Other';
  return value
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
};

const RentalPropertyTypeAnalysisCard: React.FC<{ rows?: PropertyTypeMarketRow[] }> = ({ rows }) => {
  const [filterMetric, setFilterMetric] = useState<FilterMetric>('rent');

  const data = useMemo(
    () =>
      (rows || [])
        .filter((row) => row.propertyType)
        .map((row) => ({
          propertyType: displayPropertyType(row.propertyType),
          median: Number(row.median) || 0,
          average: Number(row.average) || 0,
          perSqFt: Number(row.medianPerSqFt ?? row.averagePerSqFt) || 0,
          dom: Number(row.medianDaysOnMarket ?? row.averageDaysOnMarket) || 0,
          listings: Number(row.totalListings) || 0,
        })),
    [rows],
  );

  const sortedData = useMemo(
    () =>
      [...data].sort((a, b) => {
        if (filterMetric === 'sqft') return b.perSqFt - a.perSqFt;
        if (filterMetric === 'dom') return a.dom - b.dom;
        if (filterMetric === 'listings') return b.listings - a.listings;
        return b.median - a.median;
      }),
    [data, filterMetric],
  );

  if (!data.length) return null;

  const scaleMax = Math.max(...data.flatMap((row) => [row.median, row.average]), 1);

  return (
    <div
      style={{
        border: '1px solid #e8eef3',
        borderRadius: 14,
        background: '#fff',
        boxShadow: '0 2px 10px rgba(15, 23, 42, 0.06)',
        padding: 16,
        marginBottom: 16,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          flexWrap: 'wrap',
          marginBottom: 14,
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            color: '#12858d',
            fontSize: 18,
            fontWeight: 800,
          }}
        >
          <span
            style={{
              width: 28,
              height: 28,
              borderRadius: '50%',
              background: '#dff4f5',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 16,
            }}
          >
            ▥
          </span>
          Rental Data by Property Type
        </div>
        <div
          style={{
            display: 'flex',
            border: '1px solid #dce3e8',
            borderRadius: 8,
            overflow: 'hidden',
            height: 34,
          }}
        >
        {([
          ['rent', 'Rent'],
          ['sqft', '$/Sq Ft'],
          ['dom', 'DOM'],
          ['listings', 'Listings'],
        ] as Array<[FilterMetric, string]>).map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => setFilterMetric(key)}
            style={{
              border: 0,
              borderRight: key === 'listings' ? 0 : '1px solid #edf0f2',
              padding: '0 16px',
              background: filterMetric === key ? '#1098a0' : '#fff',
              color: filterMetric === key ? '#fff' : '#334155',
              fontSize: 12,
              fontWeight: filterMetric === key ? 700 : 500,
              cursor: 'pointer',
            }}
          >
            {label}
          </button>
        ))}
        </div>
      </div>

      <div style={{ minWidth: 0 }}>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '116px minmax(210px, 1fr) 84px 102px',
              gap: 10,
              padding: '0 0 8px',
              color: '#17233d',
              fontSize: 11,
              fontWeight: 700,
            }}
          >
            <div>Property Type</div>
            <div style={{ display: 'flex', gap: 16 }}>
              <span><span style={{ color: '#2778dd' }}>■</span> Median Rent</span>
              <span><span style={{ color: '#12a3aa' }}>●</span> Average Rent</span>
            </div>
            <div>$/sq ft</div>
            <div>Market</div>
          </div>

          {sortedData.map((row) => {
            const medianWidth = Math.max((row.median / scaleMax) * 100, row.median > 0 ? 4 : 0);
            const averagePosition = Math.min((row.average / scaleMax) * 100, 100);
            return (
              <div
                key={row.propertyType}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '116px minmax(210px, 1fr) 84px 102px',
                  gap: 10,
                  alignItems: 'center',
                  minHeight: 66,
                  borderTop: '1px solid #edf0f2',
                }}
              >
                <div style={{ fontSize: 13, fontWeight: 700, color: '#17233d' }}>{row.propertyType}</div>
                <div style={{ position: 'relative', height: 34 }}>
                  <div
                    style={{
                      position: 'absolute',
                      left: 0,
                      top: 7,
                      width: `${medianWidth}%`,
                      height: 22,
                      borderRadius: '2px 4px 4px 2px',
                      background: 'linear-gradient(90deg, #2778dd, #3288e9)',
                    }}
                  />
                  <span
                    style={{
                      position: 'absolute',
                      left: `min(calc(${medianWidth}% + 7px), calc(100% - 58px))`,
                      top: 9,
                      fontSize: 11,
                      fontWeight: 700,
                      color: '#17233d',
                    }}
                  >
                    {money(row.median)}
                  </span>
                  <span
                    style={{
                      position: 'absolute',
                      left: `min(${averagePosition}%, calc(100% - 48px))`,
                      top: -8,
                      transform: 'translateX(-5px)',
                      color: '#1098a0',
                      fontSize: 11,
                      fontWeight: 700,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    ● {money(row.average)}
                  </span>
                </div>
                <div style={{ color: '#334155', fontSize: 11 }}>
                  {row.perSqFt ? `$${row.perSqFt.toFixed(2)}/sq ft` : '—'}
                </div>
                <div style={{ display: 'flex', gap: 5 }}>
                  <div
                    style={{
                      minWidth: 44,
                      borderRadius: 6,
                      background: '#edf4ff',
                      color: '#3569a7',
                      textAlign: 'center',
                      padding: '5px 3px',
                      fontSize: 9,
                    }}
                  >
                    ▤ {row.listings}<br />Listings
                  </div>
                  <div
                    style={{
                      minWidth: 42,
                      borderRadius: 6,
                      background: '#eaf8f8',
                      color: '#167c83',
                      textAlign: 'center',
                      padding: '5px 3px',
                      fontSize: 9,
                    }}
                  >
                    ◷ {Math.round(row.dom)}<br />DOM
                  </div>
                </div>
              </div>
            );
          })}
        </div>

      <div
        style={{
          marginTop: 12,
          borderRadius: 8,
          background: '#f8fafc',
          display: 'grid',
          gridTemplateColumns: 'repeat(5, 1fr)',
          gap: 10,
          padding: 10,
          fontSize: 9,
          color: '#64748b',
        }}
      >
        <div><strong style={{ color: '#2778dd' }}>■ Median Rent</strong><br />Middle monthly rent</div>
        <div><strong style={{ color: '#1098a0' }}>● Average Rent</strong><br />Average monthly rent</div>
        <div><strong style={{ color: '#334155' }}>$/sq ft</strong><br />Rent per square foot</div>
        <div><strong style={{ color: '#3569a7' }}>▤ Listings</strong><br />Active listings</div>
        <div><strong style={{ color: '#167c83' }}>◷ DOM</strong><br />Days on market</div>
      </div>
    </div>
  );
};

export default RentalPropertyTypeAnalysisCard;
